import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useAuthContext } from "../../../contexts/AuthContext";
import { getCollaboratorLiveEvent, getLiveEvent, getLiveEventInfo } from "../../../services/liveevents";
import Loading from "../../../components/Loading";
import io from "socket.io-client";
import * as config from "../../../config.js";
import { getWebRTCToken, getCollaboratorWebRTCToken } from "services/webrtc.js";
import { getCollaboratorInfo, getCollaboratorInfoByInvitationToken, startLiveEventRecording, stopLiveEventRecording } from "services/liveevents";
import { LiveKitRoom } from "@livekit/components-react";
import { VideoConference } from "../../../components/LiveEvent/VideoConference";
import { PreJoin } from "../../../components/LiveEvent/PreJoin";
import { MetaInfo } from "../../../components/StyledElements";
import swal from "sweetalert2";

export const RecordLiveEvent = () => {
  const [webRTCToken, setWebRTCToken] = useState();
  const [recording, setRecording] = useState(null);
  const [recordButtonOn, setRecordButtonOn] = useState(false);
  const [liveEventRecording, setLiveEventRecording] = useState(null);
  const [admitted, setAdmitted] = useState(null);
  const [audioEnabled, setAudioEnabled] = useState(true);
  const [videoEnabled, setVideoEnabled] = useState(true);
  const [collaboratorInfo, setCollaboratorInfo] = useState(null);
  const [tempUser, setTempUser] = useState(false);
  const [invitationToken, setInvitationToken] = useState(null);

  const { event_id, collaborator_id } = useParams();
  const [liveEvent, setLiveEvent] = useState(null);
  const [liveEventInfo, setLiveEventInfo] = useState(null);

  const socketRef = useRef(null);
  const navigate = useNavigate();
  const { activeUser } = useAuthContext();
  const token = useSelector((state) => state.users.token);
  const collaboratorPage = window.location.href.match(/collaborator/);


  useEffect(() => {
    const fetchLiveEventInfo = async () => {
      const info = await getLiveEventInfo(event_id);
      setLiveEventInfo(info);
    }

    if (event_id) {
      fetchLiveEventInfo();
    } else {
      setLiveEventInfo(null);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event_id]);

  useEffect(() => {
    if (invitationToken) {
      const fetchCollaboratorInfo = async () => {
        const results = await getCollaboratorInfoByInvitationToken(event_id, collaborator_id, invitationToken);
        if (results.success) {
          setCollaboratorInfo(results.collaborator_info);
          setTempUser(true);
        } else {
          swal.fire({
            title: "Invalid Access Code",
            text: "The access code you entered is invalid. Please check the code and try again.",
            icon: "error",
            confirmButtonText: "OK",
          }).then((result) => {
            setInvitationToken(null);
          });
        }

      }

      fetchCollaboratorInfo();
    }
    //
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invitationToken]);

  useEffect(() => {
    if (!activeUser && (!event_id || !collaborator_id)) {
      swal.fire({
        title: "Invalid Link",
        text: "The link you used to access this live event is invalid. Please check the link and try again.",
        icon: "error",
        confirmButtonText: "OK",
      }).then((result) => {
        navigate("/live-events");
      });
      return;
    }

    if (!activeUser && !tempUser) {
      swal.fire({
        title: "Enter Access Code",
        text: "Please enter the access code to access to this live event.",
        input: "text",
        inputPlaceholder: "Access Code",
        icon: "info",
        confirmButtonText: "OK",
      }).then(async (result) => {
        setInvitationToken(result.value);
      });
      return;
    }

    if (!collaboratorInfo) return;

    if (!activeUser && collaborator_id !== collaboratorInfo.public_id) {
      swal.fire({
        title: "Access Denied",
        text: "You do not have permission to access this live event.",
        icon: "error",
        confirmButtonText: "OK",
      }).then(() => {
        navigate("/live-events");
      });

      return;
    }

    if (collaboratorInfo.role === "host" || (collaboratorInfo.status === "admitted")) {
      setAdmitted(true);
    } else {
      setAdmitted(false);
    }

    if (!socketRef.current && liveEvent) {
      const query = {};
      if (tempUser && invitationToken) {
        query.invitation_token = invitationToken;
        query.live_event_id = event_id;
        query.collaborator_id = collaborator_id;
      }

      socketRef.current = io(config.WEBSOCKET_SERVER_URL, {
        path: config.WEBSOCKET_PATH,
        transports: ["websocket"],
        reconnection: true,
        reconnectionAttempts: Infinity,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 8000,
        randomizationFactor: 0.5,
        forceNew: false,
        query: query
      });

      // baseline debug to see WHY it reconnects
      socketRef.current.on("connect", () =>
        console.log("connect", socketRef.current?.id)
      );
      socketRef.current.on("disconnect", (r) => console.log("disconnect:", r));
      socketRef.current.on("reconnect", (n) => console.log("reconnect", n));
      socketRef.current.on("reconnect_attempt", () =>
        console.log("reconnect_attempt")
      );
      socketRef.current.on("connect_error", (e) =>
        console.log("connect_error:", e?.message || e)
      );

      socketRef.current.on("recording-started", (data) => {
        console.log("!!! RECORDING STARTED: ", data);
        setRecording(true);
        setRecordButtonOn(true);
      });

      socketRef.current.on("recording-updated", (data) => {
        console.log("!!! RECORDING UPDATED: ", data);
        setRecording(true);
        setRecordButtonOn(true);
      });

      socketRef.current.on("recording-ended", (data) => {
        console.log("!!! RECORDING STARTED: ", data);
        setRecording(false);
        setRecordButtonOn(false);
      });

      if (collaboratorInfo.role === "host") {
        console.log("!!! SETTING UP HOST SOCKET EVENTS: ", liveEvent);
        socketRef.current.on("admit-recording-collaborator", (data) => {
          console.log("!!! ADMIT RECORDING COLLABORATOR: ", data, liveEvent);
          const collaboratorId = data.collaborator_id;
          const updateLiveEvent = { ...liveEvent };
          updateLiveEvent?.collaborators?.forEach((collaborator) => {
            if (collaborator.public_id === collaboratorId) {
              collaborator.status = "admitted";
            }
          });
          setLiveEvent(updateLiveEvent);
        })
        socketRef.current.on("collaborator-joined", (data) => {
          console.log("!!! COLLABORATOR JOINED: ", data);
          // Update to mark the collaborator as active
          const collaboratorId = data.collaborator_id;
          const updateLiveEvent = { ...liveEvent };
          updateLiveEvent?.collaborators?.forEach((collaborator) => {
            if (collaborator.public_id === collaboratorId) {
              collaborator.status = "active";
            }
          });
          setLiveEvent(updateLiveEvent);

        });
        socketRef.current.on("collaborator-left", (data) => {
          console.log("!!! COLLABORATOR LEFT: ", data);
          const collaboratorId = data.collaborator_id;
          const updateLiveEvent = { ...liveEvent };
          updateLiveEvent?.collaborators?.forEach((collaborator) => {
            if (collaborator.public_id === collaboratorId) {
              collaborator.status = "inactive";
            }
          });
          setLiveEvent(updateLiveEvent);
        });
      } else {
        socketRef.current.on("admitted-to-recording", (data) => {
          console.log("!!! ADMITTED TO RECORDING: ", data);
          //if (data.admitted_token) {
          setAdmitted(true);
          //}
        });
      }

      // server events
      socketRef.current.on("news", (x) => console.log("news", x));
      socketRef.current.on("welcome", (x) => console.log("welcome", x));
      // @ts-ignore catch-all
      socketRef.current.onAny((ev, ...args) => console.log("onAny:", ev, args));

      socketRef.current.on("connect", () => {
        console.log(
          "!!! CONNECTION ESTABLISHED: ",
          socketRef.current.id,
          config.WEBSOCKET_SERVER_URL
        );
        socketRef.current.emit("probe", { t: Date.now() }, (ack) => {
          console.log("!!! PROBE ACK: ", ack);
        });

        console.log("!!! EMITTING JOIN LIVE EVENT RECORDING WITH: ", {
          token,
          event_id,
          collaborator_id: collaboratorInfo?.public_id,
          invitation_token: invitationToken,
        });
        socketRef.current.emit("join-live-event-recording", {
          token: token,
          event_id: event_id,
          collaborator_id: collaboratorInfo?.public_id,
          invitation_token: invitationToken,
        }, (ack) => {
          console.log("!!! JOIN LIVE EVENT RECORDING ACK: ", ack);
        });


        console.log(
          "!!! INIT LIVE EVENT: ",
          event_id,
          liveEvent?.broadcast_token
        );
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collaboratorInfo, activeUser, tempUser, liveEvent]);

  useEffect(() => {
    console.log("!!! INITIALIZING LIVE EVENT RECORDING COMPONENT: ", { event_id, tempUser, activeUser });
    if (!event_id || (!tempUser && !activeUser)) {
      setWebRTCToken(null);
      return;
    }

    const updateCollaboratorInfo = async () => {
      const results = await getCollaboratorInfo(event_id);
      if (results.success) {
        setCollaboratorInfo(results.collaborator_info);
      }
    }

    updateCollaboratorInfo();

    // eslint-disable-next-line react-hooks/exhaustive-deps

  }, [event_id, tempUser, activeUser])

  useEffect(() => {
    console.log("!!! ADMITTED STATE CHANGED: ", admitted, { tempUser, activeUser });
    if (!tempUser && !activeUser) return;

    const fetchToken = async () => {
      setTimeout(async () => {
        const webRTCToken = tempUser ? await getCollaboratorWebRTCToken(event_id, collaboratorInfo.public_id, invitationToken) : await getWebRTCToken(event_id);
        setWebRTCToken(webRTCToken);
      }, 500);
    }

    if (admitted) {
      fetchToken();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [admitted, tempUser, activeUser]);


  useEffect(() => {
    if ((!tempUser || !invitationToken) && !activeUser) return;

    const loadRecordLiveEvent = async () => {
      // Fetch live event details from the server or context
      const liveEvent = tempUser ? await getCollaboratorLiveEvent(event_id, collaborator_id, invitationToken) : await getLiveEvent(event_id);
      console.log("!!! LIVE EVENT DETAILS: ", liveEvent);
      setLiveEvent(liveEvent);

      // Whether the live event is currently being recorded
      setRecording(liveEvent.recording);
    };

    if (event_id) {
      loadRecordLiveEvent();
      //loadEventInterval = setInterval(loadRecordLiveEvent, 10000); // Refresh every 60 seconds
    }

    return () => {
      /*if (loadEventInterval) {
        clearInterval(loadEventInterval);
      }*/
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event_id, tempUser, activeUser]);

  /*
  useEffect(() => {
    console.log("!!! RECORDING STATE CHANGED: ", recording);
    const updateRecording = async () => {
      if (recording) {
        const startedRecording = await startWebRTCRecording(event_id);
        console.log("!!! STARTED RECORDING: ", startedRecording);
      } else {
        const stoppedRecording = await stopWebRTCRecording(event_id);
        console.log("!!! STOPPED RECORDING: ", stoppedRecording);
      }
    }

    if (recording !== null) {
      updateRecording();
    }
  }, [recording]);
  */

  const onPressedRecord = async () => {
    console.log("!!! RECORD BUTTON PRESSED - TOGGLE RECORDING STATE: ", recording);
    //setRecording(!recording);
    console.log("!!! RECORDING STATE CHANGED: ", recording);
    if (!recordButtonOn) {
      console.log("!!! STARTING RECORDING FOR EVENT ID: ", event_id);
      setRecordButtonOn(true);
      const results = await startLiveEventRecording(event_id);
      console.log("!!! START RECORDING RESULTS: ", results);
      if (results.success) {
        setLiveEventRecording(results.live_event_recording);
      } else {
        swal.fire({
          title: "Error Starting Recording",
          text: "There was an error starting the recording. Please try again.",
          icon: "error",
          confirmButtonText: "OK",
        });
      }

    } else {
      setRecordButtonOn(false);
      const results = await stopLiveEventRecording(event_id, liveEventRecording.public_id);
      console.log("!!! STOP RECORDING RESULTS: ", results);
      if (results.success) {
        console.log("!!! STOPPED RECORDING: ", results.live_event_recording);
        setLiveEventRecording(results.live_event_recording);
      } else {
        swal.fire({
          title: "Error Stopping Recording",
          text: "There was an error stopping the recording. Please try again.",
          icon: "error",
          confirmButtonText: "OK",
        });
      }

    }

  };


  console.log("!!! LIVE EVENT: ", liveEvent, admitted);

  if (!liveEvent || admitted === null) {
    return <div style={{ marginTop: "50px" }}><Loading style={{ flexDirection: "column" }}><div>Loading Live Event</div><div>{liveEventInfo?.client?.name} {liveEventInfo?.title}</div></Loading ></div >;
  }

  console.log("!!! RENDERING LIVE EVENT RECORDING COMPONENT: ");

  const pageStyle = collaboratorPage ? { marginTop: "10px", height: "95vh", padding: "20px" } : { marginTop: "-20px", height: "95vh" };

  return (
    <div style={pageStyle} >
      <div style={{ display: "flex", flexDirection: "row" }}>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            width: "100%",
            height: "95vh",
          }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
            }}
          >
            <div>
              <div style={{ fontSize: "22px", fontWeight: "600", color: "var(--main-font-color)" }}>
                {liveEvent?.title ?? "Loading..."}:{" "}
                {liveEvent?.description ?? ""}
              </div>
              {
                !admitted ?
                  <div style={{ marginTop: "4px", color: "#d00", fontWeight: "600" }}>
                    You are not admitted to this live event. Please wait for the host to let you in.
                  </div>
                  : null
              }
            </div>
            {recording &&
              <div><MetaInfo style={{ borderWidth: "2px", fontWeight: "bold", color: "red", borderColor: "red", fontSize: "18px", borderRadius: "14px" }}>Recording</MetaInfo></div>
            }
          </div>
          <div
            style={{
              borderRadius: "10px",
              marginTop: "10px",
              aspectRatio: "16/9",
              position: "relative",
            }}
          >
            {
              admitted && webRTCToken ?
                <LiveKitRoom
                  token={webRTCToken}
                  serverUrl="wss://webrtc.agentwire.ai"
                  connect={true}
                  audio={audioEnabled}
                  video={videoEnabled}
                  data-lk-theme="default"
                >
                  <VideoConference
                    onPressedRecord={onPressedRecord}
                    recording={recordButtonOn}
                    liveEvent={liveEvent}
                    collaboratorInfo={collaboratorInfo}
                    onAdmitCollaborator={(collaborator) => {
                      console.log("!!! ADMITTING COLLABORATOR: ", collaborator);
                      socketRef.current.emit("admit-recording-collaborator", {
                        token: token,
                        event_id: event_id,
                        collaborator_id: collaboratorInfo.public_id,
                        admit_collaborator_id: collaborator.public_id,
                      }, (ack) => {
                        console.log("!!! ADMIT RECORDING COLLABORATOR ACK: ", ack);
                      });
                    }}
                  />
                </LiveKitRoom>
                : <PreJoin
                  style={{ padding: "0.5rem", width: "100%", backgroundColor: "black" }}
                  onSubmit={(values) => {
                    console.log("!!! PREJOIN SUBMIT: ", values);
                    setAdmitted(true);
                  }}
                  videoEnabled={videoEnabled}
                  audioEnabled={audioEnabled}
                  setVideoEnabled={(v) => {
                    console.log("TOGGLE VIDEO: ", v);
                    setVideoEnabled(v);
                  }}
                  setAudioEnabled={setAudioEnabled}
                />
            }
          </div>
        </div>
      </div>
    </div >
  );
};

export default RecordLiveEvent;
